/*
 * Trays: what will be in each card for a cycle, and printing the seals.
 *
 * The pharmacy ticks where each medicine lives when it sets the medicine up. This screen shows
 * the result before anything is made up: which cards each resident gets, what is in them, and
 * what stays in its original pack and why.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { AlignmentSheet, SealSheet } from './seal.tsx';

export type TrayItem = {
  medicineId: string;
  name: string;
  doseText: string;
  qty: number | null;
  looks: string;
  note: string;
  time: string;
  onlyOn: string;
  days: string[];
};

export type TrayCard = {
  roundId: string;
  roundName: string;
  sealName: string;
  colour: string;
  time: string;
  kind: 'tray' | 'separate';
  number: number;
  of: number;
  items: TrayItem[];
  code: string;
};

export type TrayResident = {
  residentId: string;
  residentName: string;
  room: string;
  cards: TrayCard[];
  outOfTray: Array<{ medicineId: string; name: string; reason: string }>;
  // When their seals were last printed, and whether a tray medicine has changed since.
  seals?: { lastPrintedAt: string | null; reprintNeeded: boolean };
};

type TrayData = {
  cycle: { number: number; startsOn: string; endsOn: string; days: number };
  capacity: number;
  sealOffset: { x: number; y: number };
  home: { name: string; address: string[] };
  pharmacy: { name: string; address: string[]; phone: string };
  residents: TrayResident[];
};

/*
 * With a resident, it shows just their trays — the resident's page opens it that way — and Back
 * returns to them.
 */
export function TraysScreen({ homeId, residentId, onBack }: { homeId: string; residentId?: string; onBack?: () => void }) {
  const [which, setWhich] = useState<'next' | 'current'>('next');
  const [data, setData] = useState<TrayData | null>(null);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState<TrayResident[] | null>(null);
  const [aligning, setAligning] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    try {
      const one = residentId ? `&residentId=${residentId}` : '';
      const result = await api.get<TrayData>(`/api/homes/${homeId}/trays?cycle=${which}${one}`);
      setData(result);
      setOffset(result.sealOffset ?? { x: 0, y: 0 });
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not work out the trays.');
    }
  }, [homeId, which, residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * While the seals are on screen the page itself becomes a Legal sheet with no margins, so the
   * blisters land where they do on the pharmacy's current seals.
   */
  useEffect(() => {
    if (!printing && !aligning) return;
    const style = document.createElement('style');
    style.textContent = '@media print { @page { size: 215.9mm 355.6mm; margin: 0; } }';
    document.head.append(style);
    return () => style.remove();
  }, [printing, aligning]);

  const saveOffset = useCallback(async () => {
    try {
      setError('');
      await api.post('/api/pharmacy/seal-offset', offset);
      setSaved('Saved. Every seal this printer produces uses that shift from now on.');
      setTimeout(() => setSaved(''), 4000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.');
    }
  }, [offset]);

  /*
   * The print dialog, then a note of whose seals went out. The browser cannot say whether the
   * dialog was printed or cancelled, so this assumes printed; print again if it was not.
   */
  const printNow = useCallback(async () => {
    if (!printing || !data) return;
    window.print();
    try {
      await api.post(`/api/homes/${homeId}/seal-prints`, {
        cycleStart: data.cycle.startsOn,
        residentIds: printing.map((r) => r.residentId),
      });
    } catch {
      setError('The seals printed, but the note that they did was not saved. Print them again if you are unsure.');
    }
  }, [printing, data, homeId]);

  if (aligning) {
    return (
      <section className="seals">
        <div className="head no-print">
          <div>
            <h1>Printer alignment</h1>
            <p className="muted small">
              For this pharmacy's seal printer. Print it on plain paper and hold it over an empty card.
            </p>
          </div>
          <div className="row-actions">
            <button type="button" onClick={() => window.print()}>Print the test page</button>
            <button type="button" className="quiet" onClick={() => setAligning(false)}>Back</button>
          </div>
        </div>

        <div className="card wide no-print">
          <h2>How far out is it?</h2>
          <div className="slot-top">
            <div className="narrow">
              <label htmlFor="off-x">Across (mm)</label>
              <input id="off-x" type="number" step="0.5" min="-20" max="20" value={offset.x}
                onChange={(e) => setOffset({ ...offset, x: Number(e.target.value) })} />
            </div>
            <div className="narrow">
              <label htmlFor="off-y">Down (mm)</label>
              <input id="off-y" type="number" step="0.5" min="-20" max="20" value={offset.y}
                onChange={(e) => setOffset({ ...offset, y: Number(e.target.value) })} />
            </div>
            <button type="button" onClick={() => void saveOffset()}>Save the alignment</button>
          </div>
          <p className="muted small">
            If the boxes print too far left, put a positive number in Across. Too high, positive in Down.
          </p>
          {saved ? <p className="notice">{saved}</p> : null}
        </div>

        <AlignmentSheet offset={offset} />
      </section>
    );
  }

  if (printing && data) {
    const cards = printing.reduce((n, r) => n + r.cards.length, 0);
    return (
      <section className="seals">
        <div className="head no-print">
          <div>
            <h1>{printing.length === 1 ? `Seals for ${printing[0]?.residentName}` : `Seals for ${printing.length} residents`}</h1>
            <p className="muted small">
              {cards} card{cards === 1 ? '' : 's'} · cycle starting {formatDate(data.cycle.startsOn)}
            </p>
          </div>
          <div className="row-actions">
            <button type="button" onClick={() => void printNow()}>Print</button>
            <button type="button" className="quiet" onClick={() => {
              setPrinting(null);
              void load();
            }}>Back</button>
          </div>
        </div>

        {error ? <p className="error no-print" role="alert">{error}</p> : null}
        <p className="notice no-print">
          In the print dialog: paper <strong>Legal</strong>, margins <strong>None</strong>, scale{' '}
          <strong>100%</strong>, and tick <strong>Background graphics</strong>.
        </p>

        {printing.map((resident) =>
          resident.cards.map((card) => (
            <SealSheet
              key={`${resident.residentId}-${card.roundId}-${card.kind}-${card.number}`}
              card={card}
              resident={resident}
              home={data.home}
              pharmacy={data.pharmacy}
              cycleStart={data.cycle.startsOn}
              offset={offset}
            />
          )),
        )}
      </section>
    );
  }

  const withCards = (data?.residents ?? []).filter((r) => r.cards.length);
  const outOfDate = withCards.filter((r) => r.seals?.reprintNeeded);

  return (
    <section>
      <div className="head">
        <div>
          <h1>{residentId ? `Seals for ${data?.residents[0]?.residentName ?? '…'}` : 'Seals'}</h1>
          <p className="muted small">
            Printed from the pharmacy's working version, drafts included, because the dispenser works from them.
          </p>
        </div>
        <div className="row-actions">
          {onBack ? <button type="button" className="quiet" onClick={onBack}>Back</button> : null}
          {!residentId ? (
            <button type="button" className="quiet" onClick={() => setAligning(true)}>Printer alignment</button>
          ) : null}
          <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
        </div>
      </div>

      <nav className="tabs">
        <button type="button" className={which === 'next' ? 'on' : ''} onClick={() => setWhich('next')}>
          Next cycle
        </button>
        <button type="button" className={which === 'current' ? 'on' : ''} onClick={() => setWhich('current')}>
          This cycle
        </button>
      </nav>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {!data ? <p className="muted">Working it out…</p> : null}

      {outOfDate.length ? (
        <div className="notice warn">
          <p>
            {outOfDate.length === 1
              ? `${outOfDate[0]?.residentName}'s medicines have changed since their seals were printed.`
              : `${outOfDate.length} residents' medicines have changed since their seals were printed.`}{' '}
            Reprint them so the seal on the tray matches the chart.
          </p>
          <button type="button" onClick={() => setPrinting(outOfDate)}>
            Print {outOfDate.length === 1 ? 'that one' : `just those ${outOfDate.length}`}
          </button>
        </div>
      ) : null}

      {data ? (
        <>
          <div className="row-line">
            <p className="muted">
              Cycle {data.cycle.number}: {formatDate(data.cycle.startsOn)} to {formatDate(data.cycle.endsOn)} ·{' '}
              {data.capacity} medicines fit in a blister, so a busier time of day is made up as two cards.
            </p>
            {!residentId && withCards.length > 1 ? (
              <button type="button" onClick={() => setPrinting(withCards)}>Print every seal ({withCards.length} residents)</button>
            ) : null}
          </div>

          {data.residents.map((resident) => (
            <div className="row static column" key={resident.residentId}>
              <div className="row-line">
                <span className="row-main">{resident.residentName}</span>
                <span className="chips">
                  {resident.seals?.reprintNeeded ? <span className="chip warn">Reprint needed</span> : null}
                  {resident.seals?.lastPrintedAt && !resident.seals.reprintNeeded ? (
                    <span className="chip done">Printed {formatMoment(resident.seals.lastPrintedAt)}</span>
                  ) : null}
                  {resident.room ? <span className="muted small">Room {resident.room}</span> : null}
                </span>
              </div>

              {resident.cards.length ? (
                <ul className="cards">
                  {resident.cards.map((card) => (
                    <li key={`${card.roundId}-${card.kind}-${card.number}`}>
                      <div className="card-head">
                        <span className="swatch" style={{ background: card.colour }} aria-hidden="true" />
                        <strong>{card.sealName}</strong>
                        <span className="muted small">
                          {card.kind === 'separate' ? 'separate tray' : `tray ${card.number} of ${card.of}`}
                        </span>
                      </div>
                      <ul className="tray-items">
                        {card.items.map((item) => (
                          <li key={item.medicineId}>
                            {item.qty ? `${item.qty} × ` : ''}{item.name}
                            {item.looks ? <span className="muted small"> — {item.looks}</span> : null}
                            {item.onlyOn ? <span className="chip">{item.onlyOn}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">Nothing goes in a tray for this resident.</p>
              )}

              {resident.outOfTray.length ? (
                <details>
                  <summary>{resident.outOfTray.length} medicines stay in their original packs</summary>
                  <ul className="tray-items">
                    {resident.outOfTray.map((item) => (
                      <li key={item.medicineId}>
                        {item.name}
                        <span className="muted small"> — {item.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}

              {resident.cards.length ? (
                <div className="row-actions">
                  <button type="button" onClick={() => setPrinting([resident])}>
                    {resident.seals?.reprintNeeded ? 'Reprint their seals' : 'Print their seals'}
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
}

function formatMoment(value: string): string {
  return new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}


function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
